// Face-shape reading and hair suggestions. Landmark math only — no network calls.
// Brow-to-chin / cheek width sits near 1.0 for almost every frontal face-api
// reading, so that ratio is a baseline, not a "round face" signal.

export const SHAPES = {
  oval: {
    name: "Oval",
    summary: "Proporsi wajah seimbang. Dahi, pipi, dan rahang tidak saling mengalahkan.",
    advice: "Hampir semua panjang rambut bisa duduk rapi. Layer di samping menonjolkan keseimbangan ini.",
  },
  round: {
    name: "Bulat",
    summary: "Garis wajah lembut dan lebar pipi dekat dengan lebar rahang.",
    advice: "Gaya yang menambah tinggi di mahkota, atau layer di samping, membuat wajah terasa lebih panjang.",
  },
  square: {
    name: "Persegi",
    summary: "Rahang lebar dan sudut dagu lebih tegas.",
    advice: "Tekstur dan ujung yang tidak tumpul melembutkan sudut rahang.",
  },
  heart: {
    name: "Hati",
    summary: "Dahi lebih dominan dan rahang meruncing ke dagu.",
    advice: "Volume di sekitar dagu menyeimbangkan dahi yang lebih lebar.",
  },
  oblong: {
    name: "Lonjong",
    summary: "Wajah lebih panjang daripada lebar, dengan garis vertikal yang jelas.",
    advice: "Poni, belahan samping, atau potongan sebahu memecah kesan panjang.",
  },
  diamond: {
    name: "Diamond",
    summary: "Tulang pipi paling menonjol, sementara dahi dan rahang lebih sempit.",
    advice: "Layer di pelipis mengisi dahi dan rahang supaya pipi tidak sendirian.",
  },
};

export const STYLES = [
  {
    id: "long-straight",
    gender: "wanita",
    name: "Long Straight",
    image: "c-a-straight.jpg",
    length: "panjang",
    filters: ["panjang"],
    tags: ["Lurus", "Panjang", "Rapi"],
    detail: "Rambut lurus melewati bahu, tanpa ikal.",
    shapes: { round: 1, oval: 2, heart: 1, square: 1, oblong: 4, diamond: 1 },
    softenJaw: true,
    addsBangs: false,
    addsJawWidth: false,
    why: {
      round: "Garis lurus ke bawah memanjangkan wajah yang lebar.",
      oval: "Lurus panjang jatuh rapi di wajah yang seimbang.",
      heart: "Ujung di bawah dagu mengisi rahang yang lebih sempit.",
      square: "Garis lurus menenangkan sudut rahang tanpa menambah volume.",
      oblong: "Belahan tengah memecah garis wajah yang panjang.",
      diamond: "Rambut lurus di samping mengisi jarak antara pipi dan rahang.",
    },
  },
  {
    id: "shoulder-wave",
    gender: "wanita",
    name: "Shoulder Wave",
    image: "c-a-shoulder.jpg",
    length: "sedang",
    filters: ["medium", "layer"],
    tags: ["Ombak", "Sebahu", "Lembut"],
    detail: "Gelombang sebahu. Ujungnya di sekitar bahu, bukan pixie dan bukan rambut panjang.",
    shapes: { round: 2, oval: 4, heart: 2, square: 1, oblong: 2, diamond: 1 },
    softenJaw: true,
    addsBangs: false,
    addsJawWidth: true,
    why: {
      round: "Ujung sebahu menjaga wajah tetap terbuka.",
      oval: "Ombak sebahu mengikuti garis oval.",
      heart: "Volume di ujung mengisi rahang yang ramping.",
      square: "Garis ombak melembutkan sudut rahang.",
      oblong: "Potongan sebahu memecah kesan panjang.",
      diamond: "Ombak di samping mengisi rahang yang lebih sempit.",
    },
  },
  {
    id: "short-bob",
    gender: "wanita",
    name: "Short Bob",
    image: "c-a-bob.jpg",
    length: "pendek",
    filters: ["pendek"],
    tags: ["Bob", "Dagu", "Tegas"],
    detail: "Bob sebatas dagu atau rahang. Telinga sebagian terlihat.",
    shapes: { round: 2, oval: 2, heart: 1, square: 4, oblong: 1, diamond: 2 },
    softenJaw: false,
    addsBangs: true,
    addsJawWidth: true,
    why: {
      round: "Garis bob yang tegas memecah lingkar wajah.",
      oval: "Bob dagu tetap seimbang di wajah oval.",
      heart: "Ujung di dagu menambah lebar pada rahang yang ramping.",
      square: "Potongan dagu sejalan dengan rahang yang tegas.",
      oblong: "Bob pendek memutus garis wajah yang panjang.",
      diamond: "Ujung di rahang mengisi bagian bawah yang lebih sempit.",
    },
  },
  {
    id: "short-crop",
    gender: "wanita",
    name: "Short Crop",
    image: "c-a-crop.jpg",
    length: "pendek",
    filters: ["pendek"],
    tags: ["Pendek", "Dekat", "Modern"],
    detail: "Rambut pendek dekat kepala, di atas telinga. Bukan rambut panjang.",
    shapes: { round: 4, oval: 1, heart: 1, square: 2, oblong: 1, diamond: 2 },
    softenJaw: false,
    addsBangs: false,
    addsJawWidth: false,
    why: {
      round: "Tinggi di mahkota membuat wajah terasa lebih panjang.",
      oval: "Crop pendek aman karena proporsinya sudah rata.",
      heart: "Sisakan sedikit panjang di pelipis supaya dahi tidak makin lebar.",
      square: "Garis pendek menonjolkan rahang yang sudah tegas.",
      oblong: "Jaga sedikit volume di samping agar wajah tidak makin panjang.",
      diamond: "Sisi yang rapat menonjolkan tulang pipi.",
    },
  },
  {
    id: "top-bun",
    gender: "wanita",
    name: "Top Bun",
    image: "c-a-bun.jpg",
    length: "sedang",
    filters: ["medium"],
    tags: ["Cepol", "Atas", "Ringkas"],
    detail: "Cepol di puncak kepala, dengan beberapa helai yang turun di wajah.",
    shapes: { round: 1, oval: 1, heart: 2, square: 1, oblong: 1, diamond: 4 },
    softenJaw: false,
    addsBangs: true,
    addsJawWidth: false,
    why: {
      round: "Cepol di atas mengangkat garis wajah yang lebar.",
      oval: "Cepol tetap rapi di wajah yang seimbang.",
      heart: "Helai di pelipis menyeimbangkan dahi.",
      square: "Volume di atas mengalihkan perhatian dari rahang.",
      oblong: "Tinggi di mahkota jangan berlebihan supaya wajah tidak makin panjang.",
      diamond: "Cepol di atas mengisi dahi yang lebih sempit dari tulang pipi.",
    },
  },
  {
    id: "face-frame",
    gender: "wanita",
    name: "Face Frame",
    image: "c-a-frame.jpg",
    length: "sedang",
    filters: ["medium", "layer"],
    tags: ["Frame", "Wajah", "Helai"],
    detail: "Helai rambut mengapit pipi dan rahang, sehingga wajah terbingkai.",
    shapes: { round: 1, oval: 2, heart: 4, square: 1, oblong: 2, diamond: 2 },
    softenJaw: true,
    addsBangs: true,
    addsJawWidth: true,
    why: {
      round: "Helai di samping membuka bagian tengah wajah.",
      oval: "Frame mengikuti garis wajah yang sudah rata.",
      heart: "Helai di dagu menyeimbangkan dahi yang lebih lebar.",
      square: "Garis lembut melembutkan sudut rahang.",
      oblong: "Lebar di samping memecah garis vertikal.",
      diamond: "Helai di pelipis mengisi dahi yang lebih sempit.",
    },
  },
  {
    id: "long-wave",
    gender: "wanita",
    name: "Long Wave",
    image: "c-a-long-wave.jpg",
    length: "panjang",
    filters: ["panjang", "layer"],
    tags: ["Panjang", "Ombak", "Volume"],
    detail: "Gelombang panjang melewati bahu dengan volume lembut di samping.",
    shapes: { round: 2, oval: 2, heart: 3, square: 3, oblong: 1, diamond: 2 },
    softenJaw: true,
    addsBangs: false,
    addsJawWidth: true,
    why: {
      round: "Gelombang dimulai di bawah pipi agar wajah tidak bertambah lebar.",
      oval: "Gelombang panjang mengikuti proporsi oval.",
      heart: "Volume di bawah dagu menyeimbangkan dahi.",
      square: "Ombak panjang melembutkan sudut rahang.",
      oblong: "Volume samping memecah garis wajah yang panjang.",
      diamond: "Gerakan di rahang menyeimbangkan tulang pipi.",
    },
  },
  {
    id: "layered-lob",
    gender: "wanita",
    name: "Layered Lob",
    image: "c-a-layered-lob.jpg",
    length: "sedang",
    filters: ["medium", "layer"],
    tags: ["Lob", "Layer", "Ringan"],
    detail: "Lob mendekati bahu dengan layer ringan dan ujung yang bergerak.",
    shapes: { round: 3, oval: 2, heart: 2, square: 3, oblong: 3, diamond: 1 },
    softenJaw: true,
    addsBangs: false,
    addsJawWidth: true,
    why: {
      round: "Layer vertikal membantu wajah terlihat lebih panjang.",
      oval: "Lob berlapis menjaga proporsi tetap ringan.",
      heart: "Ujung di sekitar bahu menyeimbangkan dagu.",
      square: "Ujung berlapis melembutkan garis rahang.",
      oblong: "Panjang mendekati bahu memecah kesan wajah panjang.",
      diamond: "Volume bawah mengisi rahang yang lebih sempit.",
    },
  },
  {
    id: "side-swept-bob",
    gender: "wanita",
    name: "Side-Swept Bob",
    image: "c-a-side-bob.jpg",
    length: "pendek",
    filters: ["pendek"],
    tags: ["Bob", "Belahan Samping", "Asimetris"],
    detail: "Bob pendek dengan belahan samping dan poni menyapu satu sisi.",
    shapes: { round: 3, oval: 2, heart: 3, square: 1, oblong: 3, diamond: 3 },
    softenJaw: true,
    addsBangs: true,
    addsJawWidth: true,
    why: {
      round: "Belahan diagonal memecah garis wajah yang bulat.",
      oval: "Belahan samping memberi variasi tanpa mengganggu keseimbangan.",
      heart: "Poni samping melembutkan area dahi.",
      square: "Arah diagonal mengurangi kesan rahang yang kaku.",
      oblong: "Poni samping memotong garis vertikal wajah.",
      diamond: "Poni samping mengisi area pelipis.",
    },
  },
  {
    id: "pixie-cut",
    gender: "wanita",
    name: "Pixie Cut",
    image: "c-e-pixie.jpg",
    length: "pendek",
    filters: ["pendek"],
    tags: ["Pixie", "Pendek", "Tekstur"],
    detail: "Pixie sangat pendek dengan tekstur lembut di bagian atas.",
    shapes: { round: 2, oval: 3, heart: 1, square: 3, oblong: 1, diamond: 3 },
    softenJaw: false,
    addsBangs: true,
    addsJawWidth: false,
    why: {
      round: "Tekstur di atas memberi tinggi tanpa volume di pipi.",
      oval: "Pixie membuka proporsi wajah oval.",
      heart: "Poni tipis membantu menyeimbangkan dahi.",
      square: "Tekstur lembut mengurangi kesan garis yang kaku.",
      oblong: "Poni pendek membantu memecah garis wajah panjang.",
      diamond: "Sisi rapat menonjolkan tulang pipi.",
    },
  },
  {
    id: "wispy-bob",
    gender: "wanita",
    name: "Wispy Bob",
    image: "c-a-wispy-bob.jpg",
    length: "pendek",
    filters: ["pendek", "layer"],
    tags: ["Bob", "Tipis", "Poni"],
    detail: "Bob pendek berlayer tipis dengan poni ringan di sekitar alis.",
    shapes: { round: 2, oval: 3, heart: 2, square: 2, oblong: 3, diamond: 1 },
    softenJaw: true,
    addsBangs: true,
    addsJawWidth: true,
    why: {
      round: "Layer tipis menjaga bob tidak menambah lebar berlebihan.",
      oval: "Bob ringan mengikuti proporsi oval.",
      heart: "Poni tipis melembutkan area dahi.",
      square: "Ujung ringan melembutkan rahang.",
      oblong: "Poni memecah garis wajah yang panjang.",
      diamond: "Volume di rahang menyeimbangkan tulang pipi.",
    },
  },
  {
    id: "loose-updo",
    gender: "wanita",
    name: "Loose Updo",
    image: "c-a-frame.jpg",
    length: "sedang",
    filters: ["medium"],
    tags: ["Updo", "Helai", "Lembut"],
    detail: "Rambut disanggul rendah dengan helai bergelombang yang dibiarkan turun.",
    shapes: { round: 2, oval: 2, heart: 3, square: 3, oblong: 1, diamond: 3 },
    softenJaw: true,
    addsBangs: true,
    addsJawWidth: false,
    why: {
      round: "Helai vertikal membantu wajah tampak lebih panjang.",
      oval: "Updo ringan membuka proporsi wajah.",
      heart: "Helai di pelipis menyeimbangkan dahi.",
      square: "Gelombang di samping melembutkan rahang.",
      oblong: "Jaga sanggul tetap rendah agar wajah tidak makin panjang.",
      diamond: "Helai di pelipis mengisi area dahi.",
    },
  },
  {
    id: "sleek-back",
    gender: "wanita",
    name: "Sleek Back",
    image: "c-a-crop.jpg",
    length: "sedang",
    filters: ["medium"],
    tags: ["Sleek", "Disisir Belakang", "Rapi"],
    detail: "Rambut disisir rapat ke belakang dan diikat rendah, dengan wajah terbuka.",
    shapes: { round: 1, oval: 3, heart: 1, square: 2, oblong: 1, diamond: 3 },
    softenJaw: false,
    addsBangs: false,
    addsJawWidth: false,
    why: {
      round: "Mahkota yang rapi menjaga volume jauh dari pipi.",
      oval: "Wajah oval tetap seimbang saat seluruh wajah dibuka.",
      heart: "Sisi yang rapi menonjolkan garis dahi dan dagu.",
      square: "Gaya tegas mengikuti rahang yang kuat.",
      oblong: "Hindari volume berlebih di atas.",
      diamond: "Sisi rapat menonjolkan tulang pipi.",
    },
  },
  {
    id: "textured-quiff",
    gender: "pria",
    name: "Textured Quiff",
    image: "c-a-quiff.jpg",
    length: "pendek",
    filters: ["pendek"],
    tags: ["Quiff", "Volume", "Depan"],
    detail: "Quiff dengan volume yang disisir ke atas di depan, sisi lebih pendek.",
    shapes: { round: 4, oval: 2, heart: 1, square: 1, oblong: 1, diamond: 1 },
    softenJaw: false,
    addsBangs: false,
    addsJawWidth: false,
    why: {
      round: "Tinggi di depan memecah garis bulat.",
      oval: "Volume depan menonjolkan keseimbangan wajah oval.",
      heart: "Arah ke atas mengimbangi dahi yang lebar.",
      square: "Tekstur melembutkan kesan rahang yang kaku.",
      oblong: "Volume depan memendekkan kesan wajah yang panjang.",
      diamond: "Volume depan mengisi dahi yang lebih sempit.",
    },
  },
  {
    id: "crew-cut",
    gender: "pria",
    name: "Crew Cut",
    image: "c-a-short.jpg",
    length: "pendek",
    filters: ["pendek"],
    tags: ["Crew", "Pendek", "Rata"],
    detail: "Crew cut sangat pendek, rata di atas dan rapat di samping.",
    shapes: { round: 1, oval: 1, heart: 1, square: 4, oblong: 1, diamond: 2 },
    softenJaw: false,
    addsBangs: false,
    addsJawWidth: false,
    why: {
      round: "Atas yang rata jangan terlalu lebar; sisi rapat membantu.",
      oval: "Crew cut membuka wajah yang proporsinya sudah rata.",
      heart: "Dahi terbuka perlu diimbangi bentuk dagu.",
      square: "Potongan rata sejalan dengan rahang yang tegas.",
      oblong: "Crew cut membuka wajah; jangan terlalu tipis di samping.",
      diamond: "Sisi rapat menonjolkan tulang pipi.",
    },
  },
  {
    id: "curtain-fringe",
    gender: "pria",
    name: "Curtain Fringe",
    image: "c-a-curtain.jpg",
    length: "sedang",
    filters: ["medium"],
    tags: ["Curtain", "Tengah", "Poni"],
    detail: "Poni curtain, belahan tengah, ujung jatuh ke arah alis.",
    shapes: { round: 1, oval: 4, heart: 2, square: 1, oblong: 2, diamond: 1 },
    softenJaw: true,
    addsBangs: true,
    addsJawWidth: false,
    why: {
      round: "Belahan tengah membuka bagian tengah wajah.",
      oval: "Curtain mengikuti garis oval tanpa menutup mata.",
      heart: "Poni di pelipis menyeimbangkan dahi.",
      square: "Ujung yang turun melembutkan sudut dahi.",
      oblong: "Poni memecah garis vertikal wajah yang panjang.",
      diamond: "Helai di pelipis mengisi dahi yang lebih sempit.",
    },
  },
  {
    id: "wavy-medium",
    gender: "pria",
    name: "Wavy Medium",
    image: "c-a-messy.jpg",
    length: "sedang",
    filters: ["medium", "layer"],
    tags: ["Ombak", "Sedang", "Tekstur"],
    detail: "Rambut sedang berombak, ada gerakan, tidak dicukur habis.",
    shapes: { round: 1, oval: 2, heart: 4, square: 1, oblong: 2, diamond: 2 },
    softenJaw: true,
    addsBangs: false,
    addsJawWidth: true,
    why: {
      round: "Jaga ombak lebih tinggi di atas daripada di pipi.",
      oval: "Ombak sedang tetap rapi di wajah yang seimbang.",
      heart: "Volume di samping mengisi rahang yang lebih sempit.",
      square: "Tekstur ombak melembutkan garis rahang.",
      oblong: "Lebar di samping memecah kesan panjang.",
      diamond: "Ombak di pelipis menyeimbangkan tulang pipi.",
    },
  },
  {
    id: "buzz-cut",
    gender: "pria",
    name: "Buzz Cut",
    image: "c-a-buzz.jpg",
    length: "pendek",
    filters: ["pendek"],
    tags: ["Buzz", "Habis", "Minimal"],
    detail: "Buzz cut, sangat pendek dan rata, kulit kepala hampir terlihat.",
    shapes: { round: 1, oval: 1, heart: 1, square: 2, oblong: 1, diamond: 4 },
    softenJaw: false,
    addsBangs: false,
    addsJawWidth: false,
    why: {
      round: "Buzz membuka seluruh garis wajah, jadi cocok kalau rahangnya tidak terlalu lebar.",
      oval: "Wajah oval tetap terbuka dengan rambut sependek ini.",
      heart: "Dahi yang terbuka perlu dagu yang sudah seimbang.",
      square: "Garis bersih sejalan dengan rahang yang tegas.",
      oblong: "Potongan ini membuka wajah; jangan dipakai kalau ingin memendekkan garis.",
      diamond: "Sisi yang habis menonjolkan tulang pipi.",
    },
  },
  {
    id: "neat-taper",
    gender: "pria",
    name: "Neat Taper",
    image: "c-a-taper.jpg",
    length: "pendek",
    filters: ["pendek"],
    tags: ["Taper", "Rapi", "Samping"],
    detail: "Taper rapi: samping pendek, atas sedikit lebih panjang dan disisir rapi.",
    shapes: { round: 2, oval: 2, heart: 1, square: 2, oblong: 4, diamond: 1 },
    softenJaw: false,
    addsBangs: false,
    addsJawWidth: false,
    why: {
      round: "Sedikit panjang di atas mengangkat garis wajah yang lebar.",
      oval: "Taper rapi aman di wajah yang seimbang.",
      heart: "Sisi pendek menjaga dahi tidak tertutup poni tebal.",
      square: "Garis rapi sejalan dengan rahang yang tegas.",
      oblong: "Panjang di atas memendekkan kesan wajah yang panjang.",
      diamond: "Samping rapat menonjolkan tulang pipi.",
    },
  },
  {
    id: "side-part",
    gender: "pria",
    name: "Side Part",
    image: "c-e-side.jpg",
    length: "pendek",
    filters: ["pendek"],
    tags: ["Belahan", "Samping", "Rapi"],
    detail: "Belahan samping, rambut disisir ke satu sisi.",
    shapes: { round: 1, oval: 2, heart: 2, square: 1, oblong: 2, diamond: 1 },
    softenJaw: true,
    addsBangs: true,
    addsJawWidth: false,
    why: {
      round: "Belahan samping membuka satu sisi wajah.",
      oval: "Side part klasik duduk rapi di wajah yang seimbang.",
      heart: "Rambut yang turun di pelipis menyeimbangkan dahi.",
      square: "Garis samping melembutkan sudut rahang.",
      oblong: "Belahan samping memecah garis vertikal.",
      diamond: "Panjang di pelipis mengisi dahi yang lebih sempit.",
    },
  },
  {
    id: "curly-medium",
    gender: "pria",
    name: "Curly Medium",
    image: "c-e-curly.jpg",
    length: "sedang",
    filters: ["medium", "layer"],
    tags: ["Ikal", "Sedang", "Volume"],
    detail: "Ikal sedang dengan volume. Bukan rambut lurus.",
    shapes: { round: 1, oval: 1, heart: 2, square: 1, oblong: 2, diamond: 2 },
    softenJaw: true,
    addsBangs: false,
    addsJawWidth: true,
    why: {
      round: "Jaga ikal lebih tinggi di atas daripada di pipi.",
      oval: "Ikal sedang tetap rapi di wajah yang seimbang.",
      heart: "Volume di samping mengisi rahang yang lebih sempit.",
      square: "Tekstur ikal melembutkan garis rahang.",
      oblong: "Lebar di samping memecah kesan panjang.",
      diamond: "Ikal di pelipis menyeimbangkan tulang pipi.",
    },
  },
  {
    id: "textured-crop",
    gender: "pria",
    name: "Textured Crop",
    image: "c-e-tcrop.jpg",
    length: "pendek",
    filters: ["pendek"],
    tags: ["Crop", "Tekstur", "Atas"],
    detail: "Crop pendek, samping rapat, atas bertekstur dan sedikit diangkat.",
    shapes: { round: 2, oval: 1, heart: 1, square: 2, oblong: 1, diamond: 2 },
    softenJaw: false,
    addsBangs: false,
    addsJawWidth: false,
    why: {
      round: "Tekstur di atas membuat wajah terasa lebih tinggi.",
      oval: "Crop bertekstur aman di wajah yang seimbang.",
      heart: "Volume depan mengimbangi dagu yang ramping.",
      square: "Tekstur mengalihkan perhatian dari lebar rahang.",
      oblong: "Jangan terlalu tipis di samping supaya wajah tidak makin panjang.",
      diamond: "Sisi rapat menonjolkan tulang pipi.",
    },
  },
];

export const COLORS = [
  { id: "dark-brown", name: "Dark Brown", hex: "#3A2418", undertones: ["hangat", "netral"], depths: ["cerah", "sedang", "sawo", "gelap"] },
  { id: "ash-brown", name: "Ash Brown", hex: "#6E625A", undertones: ["dingin", "netral"], depths: ["terang", "cerah", "sedang"] },
  { id: "milk-tea", name: "Milk Tea", hex: "#C2A48A", undertones: ["netral", "hangat"], depths: ["terang", "cerah", "sedang"] },
  { id: "natural-black", name: "Natural Black", hex: "#1C1A19", undertones: ["netral", "dingin", "hangat"], depths: ["sedang", "sawo", "gelap", "cerah"] },
  { id: "burgundy", name: "Burgundy", hex: "#6E2433", undertones: ["dingin", "netral"], depths: ["cerah", "sedang", "sawo", "gelap"] },
  { id: "caramel", name: "Caramel", hex: "#A56B3C", undertones: ["hangat"], depths: ["cerah", "sedang", "sawo"] },
  { id: "copper", name: "Copper", hex: "#B4532A", undertones: ["hangat"], depths: ["cerah", "sedang", "sawo"] },
  { id: "espresso", name: "Espresso", hex: "#3C2415", undertones: ["hangat", "netral"], depths: ["sedang", "sawo", "gelap"] },
  { id: "mushroom", name: "Mushroom", hex: "#A08B7E", undertones: ["dingin"], depths: ["terang", "cerah", "sedang"] },
  { id: "warm-black", name: "Warm Black", hex: "#241810", undertones: ["hangat", "netral"], depths: ["sawo", "gelap", "sedang"] },
];

export const FILTERS = [
  { id: "semua", label: "Semua" },
  { id: "pendek", label: "Pendek" },
  { id: "medium", label: "Medium" },
  { id: "panjang", label: "Panjang" },
  { id: "layer", label: "Layer" },
];

export const GOOD_PHOTOS = {
  wanita: [
    { id: "good-w1", file: "good-w1.jpg", note: "Depan, cahaya rata" },
    { id: "good-w2", file: "good-w2.jpg", note: "Wajah penuh" },
    { id: "good-w3", file: "good-w3.jpg", note: "Rambut tidak menutupi mata" },
    { id: "good-w4", file: "good-w4.jpg", note: "Dekat dan jelas" },
  ],
  pria: [
    { id: "good-m1", file: "good-m1.jpg", note: "Depan, cahaya rata" },
    { id: "good-m2", file: "good-m2.jpg", note: "Wajah penuh" },
    { id: "good-m3", file: "good-m3.jpg", note: "Dekat dan jelas" },
    { id: "good-m4", file: "good-m4.jpg", note: "Menghadap kamera" },
  ],
};

export const BAD_PHOTOS = [
  { id: "bad-angle", file: "bad-angle.jpg", note: "Menoleh" },
  { id: "bad-dark", file: "bad-dark.jpg", note: "Terlalu gelap" },
  { id: "bad-sun", file: "bad-sun.jpg", note: "Silau, kacamata" },
  { id: "bad-cover", file: "bad-cover.jpg", note: "Rambut tertutup" },
];

function dist(a, b) {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

function mid(a, b) {
  return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
}

function angleDeg(a, b, c) {
  const v1x = a.x - b.x;
  const v1y = a.y - b.y;
  const v2x = c.x - b.x;
  const v2y = c.y - b.y;
  const mag = Math.hypot(v1x, v1y) * Math.hypot(v2x, v2y) || 1;
  const cos = Math.min(1, Math.max(-1, (v1x * v2x + v1y * v2y) / mag));
  return (Math.acos(cos) * 180) / Math.PI;
}

export function measureLandmarks(pts) {
  const cheek = dist(pts[0], pts[16]) || 1;
  const jaw = dist(pts[4], pts[12]);
  const brow = dist(pts[17], pts[26]);
  const browMid = mid(mid(pts[19], pts[20]), mid(pts[23], pts[24]));
  const ratio = dist(browMid, pts[8]) / cheek;
  const jawRatio = jaw / cheek;
  const browRatio = brow / cheek;
  const chinAngle = angleDeg(pts[6], pts[8], pts[10]);
  const eyeGap = dist(mid(pts[36], pts[39]), mid(pts[42], pts[45])) / cheek;
  const lowerRatio = dist(pts[33], pts[8]) / cheek;
  const browVsJaw = brow / (jaw || 1);
  const eyeW = (dist(pts[36], pts[39]) + dist(pts[42], pts[45])) / 2;
  const eyeH = (dist(pts[37], pts[41]) + dist(pts[43], pts[47])) / 2;
  const eyeRatio = eyeW > 2 ? eyeH / eyeW : 0;
  const noseW = dist(pts[31], pts[35]) / cheek;
  const mouthW = dist(pts[48], pts[54]);
  const lipRatio = mouthW > 2 ? dist(pts[51], pts[57]) / mouthW : 0;
  const arch = ((pts[17].y + pts[26].y) / 2 - (pts[19].y + pts[24].y) / 2) / (brow || 1);
  const leftEye = mid(pts[36], pts[39]);
  const rightEye = mid(pts[42], pts[45]);
  const roll = Math.atan2(rightEye.y - leftEye.y, rightEye.x - leftEye.x) * 180 / Math.PI;
  const faceCenterX = (pts[0].x + pts[16].x) / 2;
  const yaw = (pts[30].x - faceCenterX) / cheek;
  const leftHalf = dist(pts[0], pts[30]);
  const rightHalf = dist(pts[16], pts[30]);
  const asymmetry = Math.abs(leftHalf - rightHalf) / cheek;
  return {
    ratio,
    jawRatio,
    browRatio,
    chinAngle,
    eyeGap,
    lowerRatio,
    browVsJaw,
    eyeRatio,
    noseW,
    lipRatio,
    arch,
    roll,
    yaw,
    asymmetry,
  };
}

export function classifyFace(metrics) {
  const ratio = metrics.ratio;
  const jaw = metrics.jawRatio;
  const brow = metrics.browRatio;
  const bv = metrics.browVsJaw || brow / (jaw || 1);
  const chin = metrics.chinAngle;
  const lower = metrics.lowerRatio > 0 ? metrics.lowerRatio : 0.47;
  // Face-api frontal readings cluster near ratio 1.0 and jaw 0.80.
  // Score distance from that center. The middle stays oval; only the edges
  // become round, long, heart, square, or diamond.
  const s = {
    oval: 0.35,
    round: (1 - ratio) * 14 + (0.47 - lower) * 20 + (chin - 132) / 8 + (jaw - 0.805) * 10,
    oblong: (ratio - 1.01) * 16 + (lower - 0.48) * 22,
    heart: (bv - 1.02) * 32 + (126 - chin) / 10 + (0.79 - jaw) * 14,
    square: (jaw - 0.82) * 28 + (chin - 136) / 4 + (0.98 - bv) * 14 + (ratio - 1.02) * 6,
    diamond: (0.78 - brow) * 26 + (0.785 - jaw) * 12 + (1.0 - bv) * 8,
  };
  const ranked = Object.entries(s).sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
  const margin = ranked[0][1] - ranked[1][1];
  const confidence = Math.max(0.35, Math.min(0.9, 0.45 + margin * 0.08));
  return { shape: ranked[0][0], secondary: ranked[1][0], scores: s, confidence };
}

function band(value, wide, narrow) {
  if (value > wide) return "lebar";
  if (value < narrow) return "sempit";
  return "seimbang";
}

export function readSkinTone({ r, g, b }) {
  const lightness = 0.299 * r + 0.587 * g + 0.114 * b;
  let depth = "sedang";
  if (lightness >= 185) depth = "terang";
  else if (lightness >= 150) depth = "cerah";
  else if (lightness >= 115) depth = "sedang";
  else if (lightness >= 80) depth = "sawo";
  else depth = "gelap";
  const warmth = r - b;
  const yellow = (r + g) / 2 - b;
  let undertone = "netral";
  if (warmth >= 28 && yellow >= 16) undertone = "hangat";
  else if (warmth <= 12 || b > r - 2) undertone = "dingin";
  let label = "Netral";
  if (undertone === "hangat" && warmth < 42) label = "Hangat – Netral";
  else if (undertone === "hangat") label = "Hangat";
  else if (undertone === "dingin") label = "Dingin";
  return { undertone, depth, lightness: Math.round(lightness), label, warmth };
}

function describeFace(metrics) {
  const brows = metrics.arch >= 0.08
    ? { name: "Melengkung", text: "Alis punya lengkungan yang jelas, jadi ekspresi terasa lebih terbuka." }
    : metrics.arch >= 0.035
      ? { name: "Lembut", text: "Lengkungan alis ada, tetapi tidak tajam." }
      : { name: "Lurus", text: "Alis relatif lurus, memberi kesan yang lebih tenang." };
  const eyes = metrics.eyeRatio >= 0.36
    ? { name: "Bulat", text: "Mata terbuka lebar dibanding panjangnya." }
    : metrics.eyeRatio > 0 && metrics.eyeRatio < 0.26
      ? { name: "Sipit", text: "Mata lebih panjang daripada tinggi, kesannya tenang." }
      : { name: "Almond", text: "Bentuk mata almond dengan ukuran yang proporsional, memberi kesan ekspresif dan ramah." };
  const nose = metrics.noseW >= 0.2
    ? { name: "Lebar", text: "Hidung lebih lebar dibanding pipi. Garis rambut yang bersih di samping membuatnya tetap seimbang." }
    : metrics.noseW > 0 && metrics.noseW < 0.145
      ? { name: "Ramping", text: "Hidung lebih ramping dari lebar wajah." }
      : { name: "Proporsional", text: "Lebar hidung seimbang dengan pipi dan dahi." };
  const lips = metrics.lipRatio >= 0.42
    ? { name: "Penuh", text: "Bibir penuh, jadi bagian bawah wajah terasa lebih lembut." }
    : metrics.lipRatio > 0 && metrics.lipRatio < 0.3
      ? { name: "Tipis", text: "Bibir lebih tipis. Potongan rambut yang rapi di dekat dagu menyeimbangkannya." }
      : { name: "Seimbang", text: "Bibir rapi dan seimbang dengan lebar mulut." };
  const jaw = metrics.jawRatio >= 0.84 || metrics.chinAngle >= 145
    ? { name: "Tegas", text: "Garis rahang lebar dan sudutnya lebih jelas." }
    : metrics.jawRatio < 0.75 || metrics.chinAngle <= 118
      ? { name: "Ramping", text: "Rahang meruncing ke dagu, lebih sempit dari pipi." }
      : { name: "Lembut", text: "Garis rahang lembut dan membulat, memberi kesan lebih muda dan halus." };
  const proportion = metrics.ratio >= 1.3 || metrics.lowerRatio >= 0.55
    ? { name: "Memanjang", text: "Wajah lebih panjang daripada lebar." }
    : metrics.ratio < 0.95 && metrics.jawRatio >= 0.8
      ? { name: "Melebar", text: "Lebar wajah hampir menyamai tingginya." }
      : { name: "Seimbang", text: "Proporsi dahi, pipi, dan rahang berdekatan." };
  return { brows, eyes, nose, lips, jaw, proportion };
}

function visualTraits(reading) {
  const traits = [
    reading.jaw === "lebar" ? "Rahang tegas" : reading.jaw === "ramping" ? "Rahang meruncing" : "Rahang lembut",
    reading.forehead === "lebar" ? "Area dahi lebar" : reading.forehead === "sempit" ? "Area dahi ramping" : "Dahi seimbang",
    reading.eyes === "lebar" ? "Jarak mata lebar" : reading.eyes === "rapat" ? "Jarak mata rapat" : "Jarak mata seimbang",
  ];
  if (reading.metrics.lowerRatio >= 0.52) traits.push("Bagian bawah memanjang");
  else if (reading.metrics.lowerRatio <= 0.43) traits.push("Bagian bawah ringkas");
  else traits.push("Panjang wajah seimbang");
  traits.push(`Alternatif ${SHAPES[reading.secondary]?.name || "Oval"}`);
  return traits;
}

const DEPTH_NOTE = {
  terang: "Warna kulit terang",
  cerah: "Warna kulit cerah",
  sedang: "Warna kulit sedang",
  sawo: "Warna kulit sawo matang",
  gelap: "Warna kulit lebih dalam",
};

export function colorGuideFor(skin) {
  const tone = skin?.undertone || "netral";
  if (tone === "hangat") {
    return {
      suggest: "Pilih warna rambut dengan nuansa warm brown, beige brown, atau sedikit golden tone. Undertone hangat terlihat lebih cerah, sehat, dan bernyawa.",
      avoid: "Warna yang terlalu ashy, abu-abu, platina, atau hitam pekat dapat membuat kulit terlihat lebih kusam dan wajah lebih datar.",
      avoidSwatches: [
        { id: "ash", name: "Ash Grey", hex: "#B7B1AA" },
        { id: "platinum", name: "Platinum", hex: "#E4DCD2" },
        { id: "jet", name: "Hitam Pekat", hex: "#2A2A2A" },
        { id: "blue-black", name: "Blue Black", hex: "#1A1C2A" },
      ],
    };
  }
  if (tone === "dingin") {
    return {
      suggest: "Pilih warna ashy, mushroom, atau hitam yang sedikit dingin. Undertone dingin terlihat lebih rata dengan warna yang tidak terlalu emas.",
      avoid: "Tembaga, karamel yang sangat emas, atau merah hangat bisa membuat kulit dingin terlihat kemerahan.",
      avoidSwatches: [
        { id: "copper", name: "Copper", hex: "#B4532A" },
        { id: "honey", name: "Honey", hex: "#C48A3A" },
        { id: "ginger", name: "Ginger", hex: "#C45C28" },
        { id: "gold", name: "Golden Blonde", hex: "#E0B15A" },
      ],
    };
  }
  return {
    suggest: "Kulit netral muat dengan cokelat sedang, hitam alami, maupun milk tea. Hindari ekstrem yang terlalu pucat atau terlalu biru.",
    avoid: "Platina pucat dan biru-hitam biasanya kurang menyatu dengan kulit netral.",
    avoidSwatches: [
      { id: "platinum", name: "Platinum", hex: "#E4DCD2" },
      { id: "blue-black", name: "Blue Black", hex: "#1A1C2A" },
      { id: "ash", name: "Ash Grey", hex: "#B7B1AA" },
      { id: "ginger", name: "Ginger", hex: "#C45C28" },
    ],
  };
}

export function buildReading(metrics, skin) {
  const { shape, secondary, confidence } = classifyFace(metrics);
  const jaw = metrics.jawRatio > 0.84 ? "lebar" : metrics.jawRatio < 0.75 ? "ramping" : "sedang";
  const forehead = band(metrics.browRatio, 0.9, 0.76);
  const eyes = metrics.eyeGap < 0.36 ? "rapat" : metrics.eyeGap > 0.48 ? "lebar" : "seimbang";
  const tone = skin || { undertone: "netral", depth: "sedang", lightness: 0, label: "Netral" };
  const reading = { shape, secondary, jaw, forehead, eyes, skin: tone, metrics };
  reading.visualTraits = visualTraits(reading);
  // Keep this alias while older UI/export code is migrated.
  reading.characters = reading.visualTraits;
  reading.shapeConfidence = confidence;
  const shapeInfo = SHAPES[shape];
  reading.shapeName = shapeInfo.name;
  reading.summary = `Perkiraan utama ${shapeInfo.name}, dengan alternatif ${SHAPES[secondary]?.name || "Oval"}. ${shapeInfo.summary} Rahang ${jaw}, dahi ${forehead === "seimbang" ? "seimbang dengan pipi" : forehead}, jarak mata ${eyes}.`;
  reading.shapeAdvice = shapeInfo.advice;
  reading.features = describeFace(metrics);
  reading.skinLabel = tone.label || "Netral";
  reading.skinNote = `${DEPTH_NOTE[tone.depth] || "Warna kulit"} dengan undertone ${tone.undertone}.`;
  reading.colorGuide = colorGuideFor(tone);
  return reading;
}

const STYLE_TARGET = {
  "short-crop": { ratio: 0.92, lower: 0.41, jaw: 0.81, bv: 1.0, chin: 140 },
  "short-bob": { ratio: 1.0, lower: 0.46, jaw: 0.88, bv: 0.93, chin: 152 },
  "shoulder-wave": { ratio: 1.0, lower: 0.47, jaw: 0.8, bv: 1.0, chin: 128 },
  "face-frame": { ratio: 1.0, lower: 0.46, jaw: 0.77, bv: 1.09, chin: 118 },
  "top-bun": { ratio: 1.02, lower: 0.48, jaw: 0.76, bv: 0.92, chin: 120 },
  "long-straight": { ratio: 1.1, lower: 0.54, jaw: 0.79, bv: 0.98, chin: 124 },
  "long-wave": { ratio: 1.03, lower: 0.5, jaw: 0.8, bv: 1.03, chin: 132 },
  "layered-lob": { ratio: 0.95, lower: 0.44, jaw: 0.81, bv: 1.0, chin: 136 },
  "side-swept-bob": { ratio: 1.08, lower: 0.52, jaw: 0.78, bv: 0.98, chin: 122 },
  "pixie-cut": { ratio: 1.0, lower: 0.47, jaw: 0.84, bv: 0.95, chin: 145 },
  "wispy-bob": { ratio: 1.08, lower: 0.52, jaw: 0.8, bv: 1.0, chin: 124 },
  "loose-updo": { ratio: 1.0, lower: 0.46, jaw: 0.77, bv: 1.08, chin: 120 },
  "sleek-back": { ratio: 1.02, lower: 0.48, jaw: 0.76, bv: 0.92, chin: 121 },
  "textured-quiff": { ratio: 0.92, lower: 0.41, jaw: 0.81, bv: 1.0, chin: 140 },
  "crew-cut": { ratio: 1.0, lower: 0.46, jaw: 0.88, bv: 0.93, chin: 152 },
  "curtain-fringe": { ratio: 1.0, lower: 0.47, jaw: 0.8, bv: 1.0, chin: 128 },
  "wavy-medium": { ratio: 1.0, lower: 0.46, jaw: 0.77, bv: 1.09, chin: 118 },
  "buzz-cut": { ratio: 1.02, lower: 0.48, jaw: 0.76, bv: 0.92, chin: 120 },
  "neat-taper": { ratio: 1.1, lower: 0.54, jaw: 0.79, bv: 0.98, chin: 124 },
  "side-part": { ratio: 1.08, lower: 0.52, jaw: 0.79, bv: 0.98, chin: 122 },
  "curly-medium": { ratio: 1.04, lower: 0.5, jaw: 0.78, bv: 1.06, chin: 126 },
  "textured-crop": { ratio: 1.02, lower: 0.48, jaw: 0.76, bv: 0.94, chin: 132 },
};

function metricFit(style, metrics) {
  const target = STYLE_TARGET[style.id];
  if (!target || !metrics) return 0;
  const lower = metrics.lowerRatio > 0 ? metrics.lowerRatio : 0.47;
  const bv = metrics.browVsJaw || 1;
  return -(
    Math.abs(metrics.ratio - target.ratio) * 80
    + Math.abs(lower - target.lower) * 90
    + Math.abs(metrics.jawRatio - target.jaw) * 30
    + Math.abs(bv - target.bv) * 70
    + Math.abs(metrics.chinAngle - target.chin) * 0.7
  );
}

function styleScore(style, reading) {
  if (!reading) return 0;
  return metricFit(style, reading.metrics) + (style.shapes[reading.shape] || 0) * 0.35;
}

export function styleReason(style, reading) {
  if (!reading) return style.detail;
  const line = style.why[reading.shape] || style.detail;
  if (reading.jaw === "lebar" && style.softenJaw) return `${line} Teksturnya melembutkan garis rahang.`;
  if (reading.jaw === "ramping" && style.addsJawWidth) return `${line} Ujungnya menambah volume di sekitar dagu.`;
  return line;
}

function rgbDistance(a, b) {
  const ar = parseInt(a.slice(1, 3), 16);
  const ag = parseInt(a.slice(3, 5), 16);
  const ab = parseInt(a.slice(5, 7), 16);
  const br = parseInt(b.slice(1, 3), 16);
  const bg = parseInt(b.slice(3, 5), 16);
  const bb = parseInt(b.slice(5, 7), 16);
  return Math.hypot(ar - br, ag - bg, ab - bb);
}

const OPPOSED = {
  hangat: new Set(["ash-brown", "mushroom", "burgundy"]),
  dingin: new Set(["caramel", "copper"]),
};

export function recommendColors(skin) {
  const tone = skin?.undertone || "netral";
  const depth = skin?.depth || "sedang";
  const opposed = OPPOSED[tone] || new Set();
  const ranked = COLORS.filter((color) => !opposed.has(color.id)).map((color) => {
    let score = 0;
    if (color.undertones.includes(tone)) score += 5;
    if (color.undertones.includes("netral")) score += 1;
    if (color.depths.includes(depth)) score += 4;
    return { color, score };
  }).sort((a, b) => b.score - a.score || a.color.name.localeCompare(b.color.name));

  const picked = [];
  for (const row of ranked) {
    if (picked.some((c) => rgbDistance(c.hex, row.color.hex) < 42)) continue;
    picked.push(row.color);
    if (picked.length === 6) break;
  }
  for (const row of ranked) {
    if (picked.length === 6) break;
    if (!picked.includes(row.color)) picked.push(row.color);
  }
  return picked;
}

export function colorReason(color, skin) {
  const tone = skin?.undertone || "netral";
  const depth = skin?.depth || "sedang";
  if (color.undertones.includes(tone)) {
    return `Selaras dengan undertone ${tone} pada kulit ${depth}.`;
  }
  return `Netral, tetap rapi di kulit ${depth} dengan undertone ${tone}.`;
}

export function recommendStyles(reading, gender = "wanita") {
  return STYLES.filter((style) => style.gender === gender).sort((a, b) => {
    const d = styleScore(b, reading) - styleScore(a, reading);
    if (d) return d;
    return a.name.localeCompare(b.name);
  });
}

function referenceFile(style) {
  return style?.image || "";
}

export function hasRef(style) {
  return Boolean(referenceFile(style));
}

export function topStyles(reading, gender = "wanita", count = 3) {
  const ranked = recommendStyles(reading, gender).filter(hasRef);
  const aiPicked = (reading?.aiStyleIds || [])
    .map((id) => ranked.find((style) => style.id === id))
    .filter(Boolean);
  if (aiPicked.length >= count) return aiPicked.slice(0, count);
  const seen = new Set();
  const unique = [...aiPicked];
  aiPicked.forEach((style) => seen.add(referenceFile(style)));
  for (const style of ranked) {
    if (unique.includes(style)) continue;
    const file = referenceFile(style);
    if (seen.has(file)) continue;
    seen.add(file);
    unique.push(style);
  }
  if (count < 6) return unique.slice(0, count);
  const buckets = { pendek: [], sedang: [], panjang: [] };
  for (const style of unique) {
    const key = buckets[style.length] ? style.length : "sedang";
    buckets[key].push(style);
  }
  const picked = [];
  let guard = 0;
  while (picked.length < count && guard < 12) {
    let added = false;
    for (const key of ["pendek", "sedang", "panjang"]) {
      const next = buckets[key].shift();
      if (!next) continue;
      picked.push(next);
      added = true;
      if (picked.length === count) break;
    }
    if (!added) break;
    guard += 1;
  }
  return picked;
}

export function hairSrc(file) {
  const pub = process.env.PUBLIC_URL || "";
  return `${pub}/hair/${file}`;
}

export function refImage(style) {
  return hairSrc(referenceFile(style) || "c-a-straight.jpg");
}
